import { useEffect, useState } from 'react';
import { categoryLinesApi } from '../utils/api';
import { categoryLineColor, nextCategoryLineColor } from '../utils/classificationColors';
import { deleteCategoryLineDialog } from '../utils/budgetCopy';
import ConfirmModal from './ConfirmModal';
import Icon from './Icon';
import ExpandCaret from './ExpandCaret';
import { track, EVENTS } from '../utils/analytics';

// הגדרת קטגוריות — the identity half of what used to be one CategoryLineSheet:
// create, rename, recolour, delete. Targets moved to BudgetTargetsSheet; the
// split follows the workflow ("define the lines" is a once-in-a-setup act,
// "set the targets" is monthly) and keeps each collapsible card one question.
//
// Identity edits are IMMEDIATE (one call per act, like the old sheet) — there
// is no draft state to batch, and a rename that waits for a save button is a
// rename that silently never happened.

const EMPTY_LINES = [];

export default function CategoryLineDefinitionSheet({ lines = EMPTY_LINES, open = true, onToggle, onRefresh }) {
  const [newName, setNewName] = useState('');
  const [editingName, setEditingName] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [refreshWarning, setRefreshWarning] = useState('');
  const [savedFoodCosts, setSavedFoodCosts] = useState({});
  const [confirm, setConfirm] = useState(null);
  const [pendingFoodCost, setPendingFoodCost] = useState(null);
  const [foodCostStatus, setFoodCostStatus] = useState('');

  // Keep acknowledged flags if refresh fails; fresh server rows supersede them.
  useEffect(() => { setSavedFoodCosts({}); }, [lines]);

  const run = async (fn, failure) => {
    if (busy) return false;
    setBusy(true); setError(''); setRefreshWarning('');
    try {
      try { await fn(); }
      catch (e) { setError(e?.response?.data?.error || failure); return false; }
      try { await onRefresh?.(); }
      catch { setRefreshWarning('השינוי נשמר, אך הרענון נכשל. רעננו את הדף.'); }
      return true;
    } finally { setBusy(false); }
  };

  const addLine = () => {
    const trimmed = newName.trim();
    if (!trimmed) return;
    run(async () => {
      await categoryLinesApi.create({ name: trimmed, color: nextCategoryLineColor(lines) });
      setNewName('');
    }, 'שגיאה בהוספה');
  };

  const commitRename = (line) => {
    if (busy) return;
    const trimmed = (editingName?.value || '').trim();
    setEditingName(null);
    if (!trimmed || trimmed === line.name) return;
    run(() => categoryLinesApi.update(line.id, {
      name: trimmed,
    }), 'שגיאה בשינוי השם');
  };

  const recolor = (line, value) => {
    if (busy) return;
    if (!value || value.toLowerCase() === (line.color || '').toLowerCase()) return;
    run(() => categoryLinesApi.update(line.id, {
      color: value,
    }), 'שגיאה בשינוי הצבע');
  };

  const toggleFoodCost = async (line, on) => {
    if (busy) return;
    setPendingFoodCost({ id: line.id, on });
    setFoodCostStatus('שומר…');
    const saved = await run(async () => {
      await categoryLinesApi.update(line.id, {
        food_cost: on,
      });
      setSavedFoodCosts(previous => ({ ...previous, [line.id]: on }));
      track(EVENTS.CATEGORY_FOOD_COST_TOGGLED, { on });
    }, 'שגיאה בעדכון Food Cost');
    setPendingFoodCost(null);
    setFoodCostStatus(saved ? 'נשמר' : '');
  };

  const removeLine = (line) => {
    setConfirm({
      ...deleteCategoryLineDialog({
        name: line.name,
        classificationCount: line.classification_count || 0,
        supplierCount: line.supplier_count || 0,
      }),
      onConfirm: () => { setConfirm(null); run(() => categoryLinesApi.delete(line.id), 'שגיאה במחיקה'); },
    });
  };

  return (
    <>
      {confirm && <ConfirmModal {...confirm} onCancel={() => setConfirm(null)} />}
      <section className={`card category-lines-card${open ? '' : ' is-collapsed'}`}>
        <button
          type="button" className="category-lines-toggle"
          onClick={onToggle} aria-expanded={open} aria-controls="budget-line-def-body"
        >
          <span className="category-lines-title">הגדרת קטגוריות עסק</span>
          <span className="category-lines-summary">
            {lines.length > 0 ? `${lines.length} קטגוריות` : ''}
          </span>
          <ExpandCaret open={open} size={14} />
        </button>

        {open && (
        <div id="budget-line-def-body" dir="rtl">
          {(error || refreshWarning) && (
            <div role="alert" className={error ? 'badge-red' : 'badge-yellow'} style={{ display: 'block', marginBottom: 12, padding: '8px 12px', borderRadius: 8 }}>
              {error || refreshWarning}
            </div>
          )}

          <div className="sheet-add-row">
            <div className="sheet-add-line">
              <Icon name="plus" size={15} className="sheet-add-line-icon" />
              <input
                className="sheet-add-line-input" placeholder="הוספת קטגוריה" aria-label="שם קטגוריה חדשה"
                value={newName} onChange={e => setNewName(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addLine(); } }}
              />
              <button type="button" className="sheet-add-line-btn" onClick={addLine} disabled={busy || !newName.trim()}>
                הוסף
              </button>
            </div>
          </div>

          <p id="food-cost-hint" className="food-cost-hint">Food Cost = חומרי גלם (מזון, משקאות). לא כולל שכר, שכירות, שירותים. השינויים נשמרים מיד. <span role="status">{foodCostStatus}</span></p>
          <div className="table-wrapper sheet-table" style={{ overflowY: 'auto', maxHeight: 338 }}>
            <table>
              <thead>
                <tr>
                  <th style={{ width: 34 }} />
                  <th>קטגוריה</th>
                  <th style={{ whiteSpace: 'nowrap' }}>שיוכים</th>
                  <th className="food-cost-heading">Food Cost</th>
                  <th style={{ width: 40 }} />
                </tr>
              </thead>
              <tbody>
                {lines.map((line, i) => (
                  <tr key={line.id}>
                    <td>
                      <input
                        type="color" disabled={busy} aria-label={`צבע עבור ${line.name}`}
                        value={line.color || categoryLineColor(line, i)}
                        onChange={e => recolor(line, e.target.value)}
                        style={{ width: 26, height: 22, padding: 0, border: 0, background: 'none' }}
                      />
                    </td>
                    <td>
                      {editingName?.id === line.id ? (
                        <input
                          className="form-control" autoFocus disabled={busy}
                          aria-label={`שם עבור ${line.name}`}
                          value={editingName.value}
                          onChange={e => setEditingName({ id: line.id, value: e.target.value })}
                          onKeyDown={e => {
                            if (e.key === 'Enter') { e.preventDefault(); commitRename(line); }
                            if (e.key === 'Escape') setEditingName(null);
                          }}
                          onBlur={() => commitRename(line)}
                          style={{ padding: '4px 8px' }}
                        />
                      ) : (
                        <span
                          role="button" tabIndex={busy ? -1 : 0} aria-disabled={busy}
                          onClick={() => { if (!busy) setEditingName({ id: line.id, value: line.name }); }}
                          onKeyDown={e => { if (!busy && e.key === 'Enter') setEditingName({ id: line.id, value: line.name }); }}
                          title="לחץ לשינוי השם" style={{ cursor: 'text' }}
                        >
                          {line.name}
                        </span>
                      )}
                    </td>
                    <td style={{ color: 'var(--ink-muted)', fontSize: 12.5, whiteSpace: 'nowrap' }}>
                      {(line.classification_count || 0) + (line.supplier_count || 0) > 0
                        ? [
                            line.classification_count > 0 ? `${line.classification_count} סיווגים` : null,
                            line.supplier_count > 0 ? `${line.supplier_count} ספקים` : null,
                          ].filter(Boolean).join(' · ')
                        : '—'}
                    </td>
                    <td>
                      <label className="food-cost-checkbox">
                        <input type="checkbox" aria-label={`כלול ב-Food Cost — ${line.name}`}
                          aria-describedby="food-cost-hint"
                          checked={pendingFoodCost?.id === line.id ? pendingFoodCost.on : (savedFoodCosts[line.id] ?? (line.food_cost !== false))}
                          disabled={busy} onChange={e => toggleFoodCost(line, e.target.checked)} />
                      </label>
                    </td>
                    <td style={{ width: 40, padding: '6px 4px' }}>
                      <button
                        type="button" className="btn btn-secondary"
                        onClick={() => removeLine(line)} disabled={busy}
                        aria-label={`מחק את ${line.name}`} title="מחק קטגוריה"
                        style={{ padding: '4px 7px', color: '#b91c1c' }}
                      >
                        <Icon name="trash" size={13} />
                      </button>
                    </td>
                  </tr>
                ))}
                {lines.length === 0 && (
                  <tr><td colSpan={5} style={{ color: '#94a3b8', padding: '14px 8px' }}>
                    עדיין אין קטגוריות. הוסיפו 3-10 קטגוריות שמתארים איך אתם חושבים על ההוצאה.
                  </td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
        )}
      </section>
    </>
  );
}
